import { observer } from 'mobx-react-lite';
import type React from 'react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@workspace/ui/lib/utils';

import { DeliveryTimeWarning } from '~/components/HeaderV3/components/DeliveryTimeWarning';
import { useCartButton } from '~/hooks/useCartButton';

import { HEADER_BADGE_CLASS, HEADER_ICON_CLASS } from './constants';

type HeaderDubaiExpressCartButtonProps = Omit<
  React.ComponentPropsWithoutRef<'div'>,
  'children'
> & {
  /** The cart glyph; the button around it handles the click. */
  children: React.ReactNode;
};

/**
 * Cart action of the DubaiExpress header: a button around the given glyph
 * that opens the cart, with the item count badge and the delivery-time
 * warning anchored to it.
 */
export const HeaderDubaiExpressCartButton =
  observer<HeaderDubaiExpressCartButtonProps>(
    ({ children, className, ...props }) => {
      const { t } = useTranslation();
      const buttonRef = useRef<HTMLDivElement>(null);
      const { cartBadgeLabel, handleNavigateCart } = useCartButton();

      return (
        <div {...props} className={cn('relative', className)} ref={buttonRef}>
          <button
            type="button"
            aria-label={t('cart')}
            onClick={handleNavigateCart}
            className={cn(
              HEADER_ICON_CLASS,
              'border-0 bg-transparent p-0 [font-family:inherit]',
            )}
          >
            {children}
            {cartBadgeLabel && (
              <span className={HEADER_BADGE_CLASS}>{cartBadgeLabel}</span>
            )}
          </button>
          <DeliveryTimeWarning buttonRef={buttonRef} />
        </div>
      );
    },
  );
HeaderDubaiExpressCartButton.displayName = 'HeaderDubaiExpressCartButton';
